import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DoCheck,
  HostListener,
  OnDestroy, HostBinding
} from '@angular/core';
import { interval, pipe, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { JanusService } from '@shared/services/janus.service';
import { StateService } from '@shared/services/state.service';
import { LayoutType } from '@enums';
import { CustomizationModel } from '../../models/customization.model';
import { CustomizationService } from '../../services/customization.service';
import { ensureGlobalObject } from '../../utils/ensure-global-object.util';
import { PlatformDetectorService } from '@shared/services/platform-detector/platform-detector.service';
import { MultiMedia } from '@models';
import { BrandModel } from '../../models/brand.model';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '@shared/services/app.service';

@UntilDestroy()
@Component({
  selector: 'stusan-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, DoCheck, OnDestroy {
  destroyed$ = new Subject<boolean>();
  private brand: BrandModel;
  public roomName?: string;
  isDesktopPlatform = false;
  // TODO SR remove and find a way around
  public isSirius = false;
  public customConfig: CustomizationModel;
  @HostBinding('class.sticky') isSticky = false;
  @HostBinding('class.leader') leader: boolean;
  @HostBinding('class.hidden') isHidden = true;
  public usersNumber: number | undefined;
  public isMobilePlatform = false;
  @HostBinding('class.is-participants-shown') isParticipantsShown: boolean;
  @HostBinding('class.is-chat-shown') isChatShown: boolean;
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this.isMobilePlatform) {
      this.stateService.layoutType$.next(this.layoutTypeEnum.leader);
      return;
    }
    if (this.stateService.isScreenShareEnabled) {
      this.stateService.layoutType$.next(this.layoutTypeEnum.share);
      return;
    }
    if (!this.customConfig.isLikesEnabled) {
      this.stateService.layoutType$.next(this.layoutTypeEnum.tile);
    }
  }

  constructor(
    public janusService: JanusService,
    public stateService: StateService,
    public customizationService: CustomizationService,
    private cdr: ChangeDetectorRef,
    private platformDetectorService: PlatformDetectorService,
    private readonly route: ActivatedRoute,
    private appService: AppService,
  ) {
    this.isSirius = customizationService.isSirius();
    this.customConfig = customizationService.config;
  }

  public meetingDuration = '';

  public layoutTypeEnum = LayoutType;

  ngOnInit(): void {
    // not using async directive to optimize rendering
    interval(1000).pipe(
      map((seconds: number) => {
        if (this.stateService.startTime) {
          return new Date(Date.now() - this.stateService.startTime)
            .toUTCString()
            .substring(17, 25);
        } else {
          return new Date()
            .toUTCString()
            .substring(17, 25);
        }
        // In order to get rid of time offset
      }),
      startWith('00:00:00'),
      pipe(untilDestroyed(this)),
    ).subscribe((value) => {
      this.meetingDuration = value;
      this.cdr.detectChanges();
    });

    this.isMobilePlatform = this.platformDetectorService.isMobile();
    this.stateService.roomName$.subscribe((name: any) => {
      this.stateService.webinarName$.subscribe((webinar) => {
        if (webinar) {
          this.roomName = webinar;
        } else {
          this.roomName = name;
        }
        this.cdr.markForCheck();
      });
    });
    ensureGlobalObject('APP.conference');
    this.isDesktopPlatform = this.platformDetectorService.isDesktop();
    this.stateService.isMobileMenuHidden$.asObservable().pipe(
      takeUntil(this.destroyed$),
    ).subscribe(isMobileMenuHidden => {
      this.isSticky = isMobileMenuHidden;
      // this.isHidden = isMobileMenuHidden;
    });
    this.stateService.layoutType$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((layoutType: LayoutType) => {
      this.leader = layoutType === 'leader';
    });
    this.janusService.videos$.asObservable().pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
    ).subscribe((videos: MultiMedia[] | null) => {
      this.usersNumber = videos?.filter(video => video.video.type !== 'screen'
        && !this.appService.isScreen(video.video.name)).length;
    });
    this.stateService.isParticipantsShown$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isParticipantsShown: boolean) => {
      this.isParticipantsShown = isParticipantsShown;
    });
    this.stateService.isChatShown$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isChatShown: boolean) => {
      this.isChatShown = isChatShown;
    });
  }

  public setLayoutType(layoutType: LayoutType): void {
    this.stateService.isLayoutChanged$.next(true);
    this.stateService.layoutType$.next(layoutType);
  }

  ngDoCheck(): void {
    ensureGlobalObject('APP.conference').membersCount = this.janusService.userIds.length;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
